import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';

const SettingsContext = createContext();

export const SETTINGS_KEY = 'safeguardpro_settings_v1';

export const DEFAULT_SETTINGS = {
  siteName: 'SafeguardPro',
  siteSubtitle: 'Youth Centre Management',
  siteLogo: '',
  dateFormat: 'US',
  attendanceThreshold: 70,
  defaultReportRange: '30',
  studentPortal: {
    quoteApiEnabled: false,
    quoteMode: 'fallback',
    quoteApiUrl: 'https://api.quotable.io/quotes/random?tags=motivational|inspirational&limit=1',
    quoteField: 'content',
    authorField: 'author',
    quoteApiHeaders: '',
    localQuotes: [
      { id: 'quote-1', text: 'Small steps each day add up to big changes.', author: 'SafeguardPro' },
      { id: 'quote-2', text: 'Progress over perfection.', author: 'SafeguardPro' },
      { id: 'quote-3', text: "You're allowed to take up space and ask for support.", author: 'SafeguardPro' },
      { id: 'quote-4', text: 'Showing up matters. Keep going.', author: 'SafeguardPro' },
      { id: 'quote-5', text: "Be kind to yourself - you're doing your best.", author: 'SafeguardPro' },
      { id: 'quote-6', text: 'Every day is a fresh start.', author: 'SafeguardPro' },
      { id: 'quote-7', text: 'You are capable of more than you think.', author: 'SafeguardPro' },
      { id: 'quote-8', text: 'Asking for help is a strength, not a weakness.', author: 'SafeguardPro' },
      { id: 'quote-9', text: 'Focus on what you can control today.', author: 'SafeguardPro' },
      { id: 'quote-10', text: 'Small wins are still wins.', author: 'SafeguardPro' }
    ],
    customHtmlEnabled: false,
    customHtml: '',
    bannerEnabled: false,
    bannerText: '',
    bannerSpeed: 18,
    bannerBackground: '#111827',
    bannerTextColor: '#ffffff',
    allowPrivateJournal: false,
    wellbeing: {
      enabled: true,
      frequencyDays: 7,
      promptTitle: 'Wellbeing check-in',
      promptText: 'Keep a short note on how you are feeling, and share it with your support team if needed.'
    },
    pointsEnabled: true,
    pointsScope: 'all',
    pointsProgrammeIds: [],
    pointsActivityIds: [],
    pointsRules: {
      journalEntry: 5,
      wellbeingCheckin: 8,
      assignmentSubmission: 10,
      teacherAwardDefault: 5
    },
    rewards: [
      { id: 'reward-1', points: 50, reward: 'Cinema voucher' },
      { id: 'reward-2', points: 100, reward: 'Day trip pass' },
      { id: 'reward-3', points: 200, reward: 'Leadership badge' }
    ],
    leaderboardEnabled: true,
    leaderboardScope: 'class',
    leaderboardBlurNames: true
  }
};

export const loadSettings = () => {
  if (typeof window === 'undefined') return { ...DEFAULT_SETTINGS };
  try {
    const raw = window.localStorage.getItem(SETTINGS_KEY);
    if (!raw) return { ...DEFAULT_SETTINGS };
    const parsed = JSON.parse(raw);
    return {
      ...DEFAULT_SETTINGS,
      ...parsed,
      studentPortal: {
        ...DEFAULT_SETTINGS.studentPortal,
        ...(parsed.studentPortal || {})
      }
    };
  } catch (error) {
    return { ...DEFAULT_SETTINGS };
  }
};

export const saveSettings = (settings) => {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(SETTINGS_KEY, JSON.stringify(settings));
};

export function SettingsProvider({ children }) {
  const [settings, setSettings] = useState(() => loadSettings());

  useEffect(() => {
    saveSettings(settings);
  }, [settings]);

  const value = useMemo(
    () => ({
      settings,
      setSettings
    }),
    [settings]
  );

  return <SettingsContext.Provider value={value}>{children}</SettingsContext.Provider>;
}

export function useSettings() {
  const context = useContext(SettingsContext);
  if (!context) {
    return { settings: { ...DEFAULT_SETTINGS }, setSettings: () => {} };
  }
  return context;
}
